/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import { Area, BHK, Price } from "../../../../redux-magic/sub-interfaces/sub-interfaces";

/* Component Imports */

import { Typography } from "@mui/material";
import PropertyCardConfigAndContact from "./sub-components/PropertyCardConfig";
import PropertyCardImage from "./sub-components/PropertyCardImage";
import PropertyCardLocation from "./sub-components/PropertyCardLocation";
import PropertyCardPrice from "./sub-components/PropertyCardPrice";
import PropertyCardTitle from "./sub-components/PropertyCardTitle";

/* Library Function Imports */

import PrepareConfigString from "../../../../lib/PrepareConfigString";
import PropertyCardCommission from "./sub-components/PropertyCardCommission";
import PropertyCardType from "./sub-components/PropertyCardType";

/* Styled Components */

const PropertyCardContainer = styled("div", {
	shouldForwardProp: (prop) => prop !== "source" && prop !== "isEditor",
})<{ source: string; isEditor: boolean }>(({ theme, source, isEditor }) => ({
	background: theme.palette.background.paper,
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	borderRadius: "0.75rem",
	objectFit: "contain",
	width: "100%",
	gap: "0.75rem",
	scrollSnapAlign: "start",
	flexShrink: 1,
	flexGrow: 1,
	padding: "0rem 0rem 1rem 0rem",
	boxShadow: "0px 4px 18px 0px rgba(0, 0, 0, 0.05)",
}));

const PropertyCardContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	height: "100%",
	cursor: "pointer",
	padding: "0rem 1rem 0rem 1rem",
}));

const PropertyCardInfoContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	padding: "0rem 0rem 0rem 0.5rem",
	width: "100%",
	height: "100%",
	cursor: "pointer",
}));

const PriceAndCommissionContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	padding: "1rem 1rem 1rem 1rem",
	background: "#dbf1e8",
	width: "100%",
	gap: "0.75rem",
	cursor: "pointer",
	borderRadius: "0.75rem 0.75rem 0rem 0rem",
}));

const NotesContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "flex-start",
	width: "100%",
	gap: "0.5rem",
	background: "#fff7f0",
	borderRadius: "0.75rem",
	padding: "1rem 1rem 1rem 1rem",
}));

const NotesValue = styled(Typography)(({ theme }) => ({
	fontSize: "0.825rem",
	lineHeight: "0.825rem",
	fontWeight: 400,
	color: theme.palette.text.primary,
}));

const NotesTitle = styled(Typography)(({ theme }) => ({
	fontSize: "0.75rem",
	lineHeight: "0.75rem",
	fontWeight: 500,
	color: theme.palette.text.secondary,
}));

const SubTextContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	margin: "0.5rem 0rem 0rem 0rem",
	width: "100%",
	cursor: "pointer",
	gap: "0.5rem",
	/* ipad Mini - 768 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 167)]: {
		gap: "0.65rem",
	},
	/* 860 */
	[theme.breakpoints.up("sm860")]: {
		gap: "0.75rem",
	},
	/* 910 */
	[theme.breakpoints.up("sm910")]: {
		gap: "0.85rem",
	},
	/* 1190 ~~Desktop Layout Start~~ */
	[theme.breakpoints.up("md1190")]: {
		gap: "0.75rem",
	},
}));

const PropertyCardBroadcastPreview = ({
	_id,
	dispatch,
	router,
	loading,
	transaction_type,
	property_type,
	title,
	images,
	location,
	price,
	bhk,
	no_of_seats,
	area,
	source,
	broadcast_commission,
	broadcast_notes,
}: {
	_id: ObjectId | string;
	dispatch: Function;
	router: NextRouter;
	loading: boolean;
	transaction_type: string;
	property_type: Array<string>;
	title: string;
	images: string;
	location: string;
	price: Price;
	bhk: BHK;
	no_of_seats: BHK;
	area: Area;
	source?: string;
	broadcast_commission: number;
	broadcast_notes: string;
}) => {
	/* Translations */

	const localLang = {
		notes: router.locale === "ar-AE" ? "ملاحظات" : router.locale === "kn-IN" ? "ಗಮನಿಸಿ" : "Notes",
	};

	return (
		<React.Fragment>
			{/* Property Card */}

			<PropertyCardContainer
				source={source ? source : ""}
				isEditor={false}
			>
				<PriceAndCommissionContainer>
					{/* Price */}

					<PropertyCardPrice
						router={router}
						loading={loading}
						price={price}
					/>

					{/* Commission */}

					<PropertyCardCommission
						router={router}
						loading={loading}
						commission={broadcast_commission}
						transaction_type={transaction_type}
					/>
				</PriceAndCommissionContainer>

				{/* Property Details */}

				<PropertyCardContentContainer>
					{/* Image */}

					<PropertyCardImage
						images={images}
						loading={loading}
						transaction_type={transaction_type}
					/>

					<PropertyCardInfoContainer>
						{/* Title */}

						<PropertyCardTitle
							loading={loading}
							title={title !== "" || title === undefined ? title : "Unknown Title"}
						/>

						<SubTextContainer>
							{/* Location */}

							<PropertyCardLocation
								loading={loading}
								location={location !== "" || location === undefined ? location : "Unknown Location"}
							/>

							{/* Property Type */}

							<PropertyCardType
								loading={loading}
								propertyType={property_type}
							/>

							{/* Config  */}

							<PropertyCardConfigAndContact
								loading={loading}
								config={PrepareConfigString(transaction_type, property_type, bhk, no_of_seats, area)}
								transaction_type={transaction_type ? transaction_type : ""}
								property_type={property_type ? property_type : []}
							/>
						</SubTextContainer>
					</PropertyCardInfoContainer>
				</PropertyCardContentContainer>

				{/* Notes */}

				{broadcast_notes ? (
					<div
						style={{
							width: "100%",
							height: "100%",
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "center",
							padding: "0rem 1rem 0rem 1rem",
						}}
					>
						<NotesContainer>
							<NotesTitle>{localLang.notes}</NotesTitle>
							<NotesValue>
								{broadcast_notes.length > 20 ? broadcast_notes.slice(0, 20) + "..." : broadcast_notes}
							</NotesValue>
						</NotesContainer>
					</div>
				) : null}
			</PropertyCardContainer>
		</React.Fragment>
	);
};

export default PropertyCardBroadcastPreview;
